import { MortgageFormData } from "../../../types/mortgage";
import { VALIDATION_CONSTANTS } from "../../../constants";

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

export interface ValidationRules {
  isPropertyLoanValid: () => boolean;
  isContactDetailsValid: () => boolean;
  isIncomeValid: () => boolean;
  isFormValid: () => boolean;
  focusFirstInvalidField: () => void;
}

export function useFormValidation(formData: MortgageFormData, currentUser: any): ValidationRules {
  const isValidEmail = (email: string) => {
    return VALIDATION_CONSTANTS.EMAIL_REGEX.test(email);
  };

  const isValidTelephone = (telephone: string) => {
    return VALIDATION_CONSTANTS.PHONE_REGEX.test(telephone.replace(/\s/g, ''));
  };

  const isPropertyLoanValid = () => {
    const loanToValue = formData.property.propertyValue > 0 
      ? (formData.property.loanRequired / formData.property.propertyValue) * 100 
      : 0;
    
    return formData.property.propertyValue > 0 &&
           formData.property.loanRequired >= VALIDATION_CONSTANTS.MIN_LOAN_AMOUNT &&
           formData.property.loanRequired <= 500000000 && // £5,000,000 in pennies
           loanToValue <= 80 &&
           formData.property.loanTermMonths > 0 &&
           formData.property.payRate > 0;
  };

  const isContactDetailsValid = () => {
    // All contact details are optional, but if provided must be valid
    const emailValid = formData.contact.email?.trim() === '' || !formData.contact.email || isValidEmail(formData.contact.email);
    const telephoneValid = formData.contact.telephone?.trim() === '' || !formData.contact.telephone || isValidTelephone(formData.contact.telephone);
    
    return emailValid && telephoneValid;
  };

  const isIncomeValid = () => {
    // Either UK income or foreign income (converted to sterling) is required
    const hasUkIncome = formData.income.totalIncomeNetOfTax > 0;
    const hasForeignIncome =
      formData.income.foreignIncome > 0 && formData.income.foreignCurrency !== "None";
    return hasUkIncome || hasForeignIncome;
  };

  const isFormValid = () => {
    // Property and loan details are always required
    if (!isPropertyLoanValid()) {
      return false;
    }

    // Income details are always required
    if (!isIncomeValid()) {
      return false;
    }

    // Contact details are optional, but must be valid if provided
    return isContactDetailsValid();
  };

  const focusFirstInvalidField = () => {
    // Use setTimeout to ensure validation errors are rendered first
    setTimeout(() => {
      // First, try to find error messages (red text) and scroll to their parent field
      const errorMessages = document.querySelectorAll('p.text-red-600, p.text-red-400');
      if (errorMessages.length > 0) {
        const firstError = errorMessages[0] as HTMLElement;
        const fieldContainer = firstError.closest('div');
        if (fieldContainer) {
          fieldContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Try to focus the input within this field
          const input = fieldContainer.querySelector('input, select') as HTMLElement;
          if (input && !input.hasAttribute('disabled')) {
            input.focus();
          }
          return;
        }
      }

      // Fallback: Check property and loan fields in order
      if (formData.property.propertyValue <= 0) {
        const labels = Array.from(document.querySelectorAll('label'));
        const propertyLabel = labels.find(l => l.textContent?.includes('Property value'));
        if (propertyLabel) {
          const fieldContainer = propertyLabel.closest('div');
          const input = fieldContainer?.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }
      
      if (formData.property.loanRequired < VALIDATION_CONSTANTS.MIN_LOAN_AMOUNT || formData.property.loanRequired > 500000000) {
        const labels = Array.from(document.querySelectorAll('label'));
        const loanLabel = labels.find(l => l.textContent?.includes('Loan amount'));
        if (loanLabel) {
          const fieldContainer = loanLabel.closest('div');
          const input = fieldContainer?.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }
      
      // Check LTV validation
      const loanToValue = formData.property.propertyValue > 0 
        ? (formData.property.loanRequired / formData.property.propertyValue) * 100 
        : 0;
      if (loanToValue > 80) {
        // Try to find LTV field or loan amount field
        const labels = Array.from(document.querySelectorAll('label'));
        const ltvLabel = labels.find(l => l.textContent?.includes('Loan to Value') || l.textContent?.includes('LTV'));
        if (ltvLabel) {
          const fieldContainer = ltvLabel.closest('div');
          if (fieldContainer) {
            fieldContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
        // Fallback to loan amount field
        const loanLabel = labels.find(l => l.textContent?.includes('Loan amount'));
        if (loanLabel) {
          const fieldContainer = loanLabel.closest('div');
          const input = fieldContainer?.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }
      
      if (formData.property.loanTermMonths <= 0) {
        const labels = Array.from(document.querySelectorAll('label'));
        const termLabel = labels.find(l => l.textContent?.includes('Loan term'));
        if (termLabel) {
          const fieldContainer = termLabel.closest('div');
          const input = fieldContainer?.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }
      
      if (formData.property.payRate <= 0) {
        const labels = Array.from(document.querySelectorAll('label'));
        const payRateLabel = labels.find(l => l.textContent?.includes('Pay rate'));
        if (payRateLabel) {
          const fieldContainer = payRateLabel.closest('div');
          const input = fieldContainer?.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }

      // Check income fields - either UK or foreign income required
      if (!isIncomeValid()) {
        const labels = Array.from(document.querySelectorAll('label'));
        // Prefer focusing Foreign Income if they've selected a currency but not entered amount
        const hasForeignCurrencySelected = formData.income.foreignCurrency !== "None";
        const foreignIncomeLabel = hasForeignCurrencySelected
          ? labels.find(l => l.textContent?.includes('Foreign Income'))
          : null;
        if (foreignIncomeLabel) {
          const fieldContainer = foreignIncomeLabel.closest('div');
          const input = fieldContainer?.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
        const incomeLabel = labels.find(l => l.textContent?.includes('Total annual income net of tax'));
        if (incomeLabel) {
          const fieldContainer = incomeLabel.closest('div');
          const input = fieldContainer?.querySelector('input') as HTMLElement;
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }
      
      // Check contact details - all optional but must be valid if provided
      if (formData.contact.email && formData.contact.email.trim() !== '' && !isValidEmail(formData.contact.email)) {
        const element = document.querySelector('input[type="email"]') as HTMLInputElement;
        if (element) {
          element.focus();
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      
      if (formData.contact.telephone && formData.contact.telephone.trim() !== '' && !isValidTelephone(formData.contact.telephone)) {
        const element = document.querySelector('input[type="tel"]') as HTMLInputElement;
        if (element) {
          element.focus();
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
    }, 100); // Small delay to ensure errors are rendered
  };

  return {
    isPropertyLoanValid,
    isContactDetailsValid,
    isIncomeValid,
    isFormValid,
    focusFirstInvalidField,
  };
}
